"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrCADDsnTransformProjectGenerator = void 0;
const TransformProject_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProject");
const TransformProcessingResult_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProcessingResult");
const OrcadDsnUtils_1 = require("./OrcadDsnUtils");
const orcadValidation_1 = require("../../../orcadValidation");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const sigma_engine_shared_2 = require("@cadstrom/sigma-engine-shared");
const TransformProjectLayerSet_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProjectLayerSet");
const orcadLayers_1 = require("../../../orcadLayers");
const orcadLogicalSchematicReferenceResolver_1 = require("../../../orcadLogicalSchematicReferenceResolver");
function compareByCodeUnit(left, right) {
    if (left === right) {
        return 0;
    }
    return left < right ? -1 : 1;
}
class OrCADDsnTransformProjectGenerator {
    constructor() {
        this.runsFor = null;
    }
    *generate(_trigger, rootContext) {
        const contexts = this.detectRootDesign(rootContext, this.detectLogicalSchemas(rootContext));
        for (const context of contexts) {
            const project = new TransformProject_1.TransformProject({
                name: 'OrCAD DSN Project',
                type: 'orcad',
            }, context);
            yield project;
            for (const message of context.unresolvedHierarchyReferences ?? []) {
                yield new TransformProcessingResult_1.TransformProcessingResult(project, {
                    type: 'orcad-hierarchy-reference-unresolved',
                    severity: sigma_engine_shared_2.BoundaryLevel.Warning,
                    message,
                });
            }
            for (const message of context.ambiguousRootRequests ?? []) {
                yield new TransformProcessingResult_1.TransformProcessingResult(project, {
                    type: 'orcad-root-request-ambiguous',
                    severity: sigma_engine_shared_2.BoundaryLevel.Warning,
                    message,
                });
            }
            yield new TransformProjectLayerSet_1.TransformProjectLayerSet(project, {
                type: 'layerSet',
                id: orcadLayers_1.ORCAD_SCH_LAYERSET,
                layerSetType: 'schematic',
                layers: orcadLayers_1.OrCadSchLayers.map((layer) => ({
                    ...layer,
                    defaultVisible: typeof layer.defaultVisible !== 'boolean' || layer.defaultVisible === true,
                })),
            });
        }
    }
    detectLogicalSchemas(rootContext) {
        const schemas = new Map();
        for (const [sourceFile, descr] of Object.entries(rootContext.parsedDef.schematics)) {
            if (!descr.validationResult?.isValid ||
                !descr.validationResult.data?.metadata?.[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema]) {
                continue;
            }
            const schema = descr.validationResult.data.metadata[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema];
            schemas.set(sourceFile, schema);
        }
        return schemas;
    }
    detectRootDesign(rootContext, logicalSchemas) {
        const schematicHierarchies = [];
        rootContext.unresolvedHierarchyReferences = [];
        const pageChildren = new Map();
        const designSourceFiles = new Map();
        for (const [sourceFile, design] of logicalSchemas) {
            designSourceFiles.set(design, sourceFile);
        }
        const requestedSourceFile = rootContext.options.requestedSourceFile;
        const requestedMatches = requestedSourceFile === undefined ?
            []
            : Array.from(logicalSchemas.entries())
                .filter(([sourceFile]) => (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(sourceFile) ===
                (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(requestedSourceFile))
                .sort(([left], [right]) => compareByCodeUnit(left, right));
        const requestedDesign = requestedMatches[0]?.[1];
        if (requestedSourceFile !== undefined) {
            if (requestedDesign === undefined) {
                throw new Error(`Requested OrCAD source file was not found: ${requestedSourceFile}`);
            }
            const matchedSourceFiles = requestedMatches.map(([sourceFile]) => sourceFile);
            if (matchedSourceFiles.length > 1) {
                rootContext.ambiguousRootRequests = [
                    `Requested OrCAD source file "${requestedSourceFile}" matched multiple OrCAD source files (${matchedSourceFiles.join(', ')}); rooted the project at ${matchedSourceFiles[0]}.`,
                ];
            }
        }
        const hierarchyRefs = new Map();
        const logicalSchematicCandidates = Array.from(logicalSchemas, ([sourceFile, design]) => ({
            sourceFile,
            schematicViewNames: design.Schematic.map((schematic) => schematic.Name),
        }));
        const resolveTargetSourceFile = (sourceFile, targetFile, targetViewName) => {
            if (!targetFile) {
                return sourceFile;
            }
            const sourceStem = (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(sourceFile).replace(/\.(dsn|xml)$/i, '');
            const targetStem = (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(OrcadDsnUtils_1.OrcadDsnUtils.resolveFilePath(sourceFile, targetFile)).replace(/\.(dsn|xml)$/i, '');
            if (sourceStem === targetStem) {
                return sourceFile;
            }
            const resolution = (0, orcadLogicalSchematicReferenceResolver_1.resolveOrCadLogicalSchematicReference)(logicalSchematicCandidates, sourceFile, targetFile, targetViewName);
            return resolution.status === 'resolved' ? resolution.candidate.sourceFile : undefined;
        };
        for (const [sourceFile, design] of logicalSchemas) {
            for (const schematic of design.Schematic) {
                for (const page of schematic.Page) {
                    const schematicHierarchy = {
                        name: OrcadDsnUtils_1.OrcadDsnUtils.getRefViewName(sourceFile, undefined, schematic.Name, page.Name),
                        sourceFile: OrcadDsnUtils_1.OrcadDsnUtils.resolveFilePath(sourceFile),
                        schematicIndex: 0,
                        children: [],
                        parents: [],
                        hierarchyIndex: 0,
                        design,
                        schematic,
                        page,
                        slug: `${sigma_engine_shared_1.Files.basenamePath(sourceFile)}_${schematic.Name}_${page.Name}`.replace(/[^a-zA-Z0-9._-]/g, '_'),
                    };
                    const pageChildrenArr = [];
                    pageChildren.set(schematicHierarchy, pageChildrenArr);
                    schematicHierarchies.push(schematicHierarchy);
                    for (const inst of page.DrawnInst ?? []) {
                        if (inst.ContentsLibName || inst.ContentsViewName) {
                            const targetSourceFile = resolveTargetSourceFile(sourceFile, inst.ContentsLibName || undefined, inst.ContentsViewName);
                            if (!targetSourceFile) {
                                rootContext.unresolvedHierarchyReferences.push(`Referenced schematic could not be resolved: ${inst.ContentsLibName ?? inst.ContentsViewName ?? inst.Name}`);
                                continue;
                            }
                            const targetFile = OrcadDsnUtils_1.OrcadDsnUtils.resolveFilePath(targetSourceFile);
                            const viewName = OrcadDsnUtils_1.OrcadDsnUtils.getRefSchematicPrefix(targetSourceFile, undefined, inst.ContentsViewName);
                            const arr = hierarchyRefs.get(viewName) ?? [];
                            arr.push(schematicHierarchy);
                            hierarchyRefs.set(viewName, arr);
                            pageChildrenArr.push({
                                name: `${viewName}::${inst.Name}`,
                                sheetSymbol: inst,
                                targetFile,
                                targetSchematic: inst.ContentsViewName,
                            });
                        }
                    }
                    for (const inst of page.PartInst ?? []) {
                        const targetSourceFile = resolveTargetSourceFile(sourceFile, inst.ContentsLibName || undefined, inst.ContentsViewName);
                        if (inst.ContentsViewType === 1) {
                            if (!targetSourceFile) {
                                rootContext.unresolvedHierarchyReferences.push(`Referenced schematic could not be resolved: ${inst.ContentsLibName ?? inst.ContentsViewName ?? inst.Name}`);
                                continue;
                            }
                            const targetFile = OrcadDsnUtils_1.OrcadDsnUtils.resolveFilePath(targetSourceFile);
                            const viewName = OrcadDsnUtils_1.OrcadDsnUtils.getRefSchematicPrefix(targetSourceFile, undefined, inst.ContentsViewName);
                            const arr = hierarchyRefs.get(viewName) ?? [];
                            arr.push(schematicHierarchy);
                            hierarchyRefs.set(viewName, arr);
                            pageChildrenArr.push({
                                name: `${viewName}::${inst.Name}`,
                                sheetSymbol: inst,
                                targetFile,
                                targetSchematic: inst.ContentsViewName,
                            });
                        }
                    }
                }
            }
        }
        for (const [hierarchyName, hierarchyEntries] of Array.from(hierarchyRefs)) {
            if (hierarchyEntries.length === 0) {
                continue;
            }
            const normalizedHierarchyName = hierarchyName.toLowerCase();
            const sourceFileHierarchies = schematicHierarchies.filter((hierarchy) => hierarchy.name.toLowerCase().startsWith(`${normalizedHierarchyName}::`));
            if (!sourceFileHierarchies.length) {
                for (const parent of hierarchyEntries) {
                    rootContext.unresolvedHierarchyReferences.push(`Referenced schematic view was not found: ${hierarchyName} (from ${parent.name})`);
                }
                continue;
            }
            for (const parent of hierarchyEntries) {
                for (const sourceFileHierarchy of sourceFileHierarchies) {
                    parent.children.push(sourceFileHierarchy);
                    sourceFileHierarchy.parents.push(parent);
                }
            }
            hierarchyRefs.delete(hierarchyName);
        }
        const rootDesigns = new Map();
        for (const hierarchy of schematicHierarchies) {
            if (requestedDesign && hierarchy.design !== requestedDesign) {
                continue;
            }
            if (!requestedDesign && hierarchy.parents.length > 0) {
                continue;
            }
            if (hierarchy.schematic.Name !== hierarchy.design.RootName) {
                continue;
            }
            const designArr = rootDesigns.get(hierarchy.design) ?? [];
            designArr.push(hierarchy);
            rootDesigns.set(hierarchy.design, designArr);
        }
        const orderedRootDesigns = Array.from(rootDesigns.entries()).sort(([left], [right]) => compareByCodeUnit(designSourceFiles.get(left) ?? left.Name, designSourceFiles.get(right) ?? right.Name));
        rootContext.detectedRootSourceFiles = orderedRootDesigns.map(([design]) => designSourceFiles.get(design) ?? design.Name);
        return orderedRootDesigns.map(([rootDesign, rootHierarchies]) => {
            const rootSchematic = rootDesign.Schematic.find((s) => s.Name === rootDesign.RootName);
            if (!rootSchematic) {
                throw new Error(`Root schematic not found`);
            }
            const hierarchyPortDiagnostics = new Set();
            rootContext.hierarchyPortDiagnosticSets = rootContext.hierarchyPortDiagnosticSets ?? [];
            rootContext.hierarchyPortDiagnosticSets.push(hierarchyPortDiagnostics);
            return {
                ...rootContext,
                hierarchyPortDiagnostics,
                rootDesign: rootDesign,
                rootSchematic,
                rootHierarchies,
                allHierarchies: schematicHierarchies,
                pageChildren,
                designSourceFiles,
                instanceSchematicMap: new Map(),
            };
        });
    }
}
exports.OrCADDsnTransformProjectGenerator = OrCADDsnTransformProjectGenerator;
//# sourceMappingURL=OrcadDsnTransformProjectGenerator.js.map